import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import GameEvent from "../../../core/application/GameEvent";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import HubEvent from "../../hub/event/HubEvent";
import SoundConfig from "../../sound/model/SoundConfig";
import TimerEvent from "../event/TimerEvent";

export default class TimerMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {LayoutManager} layoutManager 
     * @param {RevisableLanguagePack} languagePack 
     * @param {SoundConfig} soundConfig
     * @param {SoundManager} soundManager
     */
    constructor(target, dispatcher, clickEaterFactory, layoutManager, languagePack, timerConfig, soundConfig, soundManager) {
        super(target, dispatcher, clickEaterFactory, layoutManager, soundManager, soundConfig);
        this._languagePack = languagePack;
        this._timerConfig = timerConfig;
        this._soundConfig = soundConfig;
        this._soundManager = soundManager;
    }

    initialize() {
        super.initialize();
        // Timer configs
        this._reminderInterval = this._timerConfig.getValue("reminderInterval") * 1000;
        this._reminderLasting = this._timerConfig.getValue("reminderLasting") * 1000;
        this._timerValue = this._timerConfig.getValue("timerValue") * 1000;
        // Events
        this.registerEventListener(TimerEvent.START_TIMER, this.start.bind(this));
        this.registerEventListener(TimerEvent.RESTART_TIMER, this.restart.bind(this));
        this.registerEventListener(TimerEvent.STOP_TIMER, this.stop.bind(this));
        this.registerEventListener(TimerEvent.PAUSE_TIMER, this.pause.bind(this));
        this.registerEventListener(GameEvent.TIME_CHANGE, this._handleTicking.bind(this));
        this.registerEventListener(HubEvent.SHOW_HUB, this._handleShowHub.bind(this));
        this.registerEventListener(HubEvent.HIDE_HUB, this._handleHideHub.bind(this));

        // Texts for timers
        this._startText = this._languagePack.getText(`hub.timer.startLabel`);
        this._reminderText = this._languagePack.getText(`hub.timer.reminderLabel`);
        this._timeOutText = this._languagePack.getText(`hub.timer.timeOutLabel`);
        this._tweenTarget = createjs.Tween.get(this._target);
        this._shownPosition = this._target.y;
        this._hiddenPosition = this._shownPosition - TimerMediator.HIDDEN_Y_OFFSET;
        this.stop();
    }

    stop() {
        this._target.textField.text = "";
        this._started = false;
        this._startTime = null;
        this._target.gotoAndStop(1);
        this._target.alpha = 0;
        this._hide();
        this._stopUrgencyBackgroundAudio();
        this._isUrgent = false;
    }

    start() {
        this._started = true;
        this._target.alpha = 1;
    }

    restart() {
        stop();
        start();
    }

    pause() {
        // pause ticking
    }

    _handleTicking(data) {
        if (this._started) {
            let currentTime = data.params.data.time;
            if (this._startTime === null) {
                this._startTime = currentTime;
                this._nextIntervalTimeShow = this._startTime + TimerMediator.INITIAL_DELAY;
                this._nextIntervalTimeHide = this._startTime + this._reminderLasting;
                this._endTime = this._startTime + this._timerValue;
            }
            if (currentTime >= this._endTime) {
                // End state
                this.changeText(this._timeOutText, true);
                this._show(0, TimerMediator.ANIMATION_TIME);
                this._started = false;
                this._isUrgent = true;
                this._playUrgencyBackgroundAudio();
            } else if (currentTime >= this._nextIntervalTimeHide) {
                // Hide interval
                this._hide(0, TimerMediator.ANIMATION_TIME);
                this._nextIntervalTimeHide = this._nextIntervalTimeShow + this._reminderLasting;
            } else if (currentTime >= this._nextIntervalTimeShow) {
                // Show interval
                let amountOfMinutesLeft = Math.round((this._timerValue - (currentTime - this._startTime)) / 60000);
                if (this._target.textField.text == "") {
                    this.changeText(this._startText);
                } else {
                    this.changeText(this._reminderText);
                }
                this._show(0, TimerMediator.ANIMATION_TIME);
                this._nextIntervalTimeShow = currentTime + this._reminderInterval;
            }
        }
    }

    _hide(wait = 0, delay = 0) {
        this._animateY(wait, delay, this._hiddenPosition);
    }

    _show(wait = 0, delay = 0) {
        this._animateY(wait, delay, this._shownPosition);
    }

    _animateY(wait, delay, newYPosition) {
        createjs.Tween.get(this._target)
            .wait(wait)
            .to({ y: newYPosition }, delay);
    }

    changeText(text = "", urgent = false) {
        this._target.textField.text = text;

        if (urgent) {
            this._target.gotoAndStop(1);
        } else {
            this._target.gotoAndStop(0);
        }
    }

    destroy() {
        this._stopUrgencyBackgroundAudio();
        super.destroy();
    }

    _playUrgencyBackgroundAudio() {
        this._playBackgroundAudio(TimerMediator.KD_URGENT_AUDIO_ID);
    }

    _stopUrgencyBackgroundAudio() {
        this._stopBackgroundAudio();
    }

    _handleShowHub() {
        if (this._isUrgent) {
            this._playBackgroundAudio(TimerMediator.KD_URGENT_AUDIO_ID);
        }
    }

    _handleHideHub() {
        this._stopBackgroundAudio();
    }
}


TimerMediator.HIDDEN_Y_OFFSET = 100;
TimerMediator.ANIMATION_TIME = 500;
TimerMediator.INITIAL_DELAY = 2000;
TimerMediator.KD_URGENT_AUDIO_ID = "k_d_urgent";